import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cookie, X, Check, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  CONSENT_EVENT,
  CONSENT_OPEN_EVENT,
  getConsent,
  setGranularConsent,
  type ConsentState,
} from '@/lib/metaPixel';

export default function CookieConsentBanner() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const [visible, setVisible] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [hasDecided, setHasDecided] = useState(false);

  const sync = useCallback(() => {
    const c = getConsent();
    setHasDecided(!!c);
    setMarketing(c?.marketing || false);
  }, []);

  useEffect(() => {
    sync();
    if (getConsent() === null) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [sync]);

  useEffect(() => {
    const open = () => {
      sync();
      setVisible(true);
    };
    const onSync = () => sync();
    window.addEventListener(CONSENT_OPEN_EVENT, open);
    window.addEventListener(CONSENT_EVENT, onSync);
    return () => {
      window.removeEventListener(CONSENT_OPEN_EVENT, open);
      window.removeEventListener(CONSENT_EVENT, onSync);
    };
  }, [sync]);

  const save = useCallback((value: boolean) => {
    setGranularConsent({ essential: true, marketing: value });
    setMarketing(value);
    setHasDecided(true);
    setVisible(false);
  }, []);

  if (!visible) return null;

  const t = {
    title: lang === 'en' ? 'Cookie preferences' : 'Preferencias de cookies',
    body:
      lang === 'en'
        ? 'We use essential cookies for the site to work and marketing cookies (Meta Pixel) to measure campaigns. You can change this choice whenever you want.'
        : 'Usamos cookies esenciales para que el sitio funcione y cookies de marketing (Meta Pixel) para medir campañas. Podés cambiar esta preferencia cuando querás.',
    privacy: lang === 'en' ? 'Privacy policy' : 'Política de privacidad',
    rights: lang === 'en' ? 'Data rights' : 'Derechos de datos',
    decline: lang === 'en' ? 'Decline all' : 'Rechazar todo',
    accept: lang === 'en' ? 'Accept selected' : 'Aceptar selección',
    close: lang === 'en' ? 'Close' : 'Cerrar',
    essential: lang === 'en' ? 'Essential' : 'Esenciales',
    essentialDesc:
      lang === 'en'
        ? 'Session, cart, language and security. Always active.'
        : 'Sesión, carrito, idioma y seguridad. Siempre activas.',
    marketing: lang === 'en' ? 'Marketing / Analytics' : 'Marketing / Analítica',
    marketingDesc:
      lang === 'en'
        ? 'Meta Pixel to measure ads and conversions.'
        : 'Meta Pixel para medir anuncios y conversiones.',
    statusOn: lang === 'en' ? 'Marketing cookies: on' : 'Cookies de marketing: activas',
    statusOff: lang === 'en' ? 'Marketing cookies: off' : 'Cookies de marketing: inactivas',
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.title}
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 animate-in slide-in-from-bottom duration-500"
    >
      <div className="glass relative mx-auto flex max-w-3xl flex-col gap-4 rounded-xl border border-primary/30 p-4 pr-10 shadow-lg">
        <div className="flex items-start gap-3">
          <Cookie className="h-6 w-6 shrink-0 text-primary" aria-hidden="true" />
          <div className="flex-1 space-y-1">
            <h2 className="font-display text-sm font-bold">{t.title}</h2>
            <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">
              {t.body}{' '}
              <Link to="/privacidad" className="text-primary underline underline-offset-2 hover:text-primary/80">
                {t.privacy}
              </Link>
              {' · '}
              <Link to="/privacidad/derechos" className="text-primary underline underline-offset-2 hover:text-primary/80">
                {t.rights}
              </Link>
            </p>
          </div>
        </div>

        <div className="space-y-3 rounded-lg border border-border/50 bg-muted/30 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <Shield className="h-4 w-4 mt-0.5 text-success" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium">{t.essential}</p>
                <p className="text-xs text-muted-foreground">{t.essentialDesc}</p>
              </div>
            </div>
            <Switch checked disabled aria-label={t.essential} />
          </div>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 mt-0.5 text-primary" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium">{t.marketing}</p>
                <p className="text-xs text-muted-foreground">{t.marketingDesc}</p>
              </div>
            </div>
            <Switch
              checked={marketing}
              onCheckedChange={setMarketing}
              aria-label={t.marketing}
            />
          </div>
        </div>

        {hasDecided && (
          <p className="text-[11px] font-medium uppercase tracking-wide text-primary/80">
            {marketing ? t.statusOn : t.statusOff}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => save(false)}>
            {t.decline}
          </Button>
          <Button size="sm" className={marketing ? 'neon-glow' : ''} onClick={() => save(marketing)}>
            {t.accept}
          </Button>
        </div>

        {hasDecided && (
          <button
            type="button"
            aria-label={t.close}
            onClick={() => setVisible(false)}
            className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
